import { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Space,
  Button,
  Typography,
  Alert,
  List,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Table,
  Tabs,
  Empty,
  Avatar,
  Upload,
  message,
  Spin,
} from 'antd';
import type { UploadProps } from 'antd';
import {
  WalletOutlined,
  AppstoreOutlined,
  ExperimentOutlined,
  RiseOutlined,
  EditOutlined,
  LockOutlined,
  CheckCircleFilled,
  GiftOutlined,
  BankOutlined,
  UserOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { formatCents, formatDate } from '@/utils/format';
import request from '@/utils/request';
import type {
  BankCardDTO,
  CreatorTaskDTO,
  WithdrawalDTO,
  NativePayInitiation,
  DevSimulateInitiation,
  PaymentOrderStatus,
} from '@zero/shared';

type TopupInitResp = NativePayInitiation | DevSimulateInitiation;

interface OrderStatusResp {
  outTradeNo: string;
  status: PaymentOrderStatus;
  paidAt: string | null;
}

interface HomeSummary {
  experimentsCount: number;
  publishedBundlesCount: number;
  totalSalesCount: number;
  totalEarningsCents: number;
}

interface Txn {
  id: number;
  walletType: 'experiment' | 'earnings';
  kind: string;
  amountCents: number;
  balanceAfterCents: number;
  note: string | null;
  createdAt: string;
}

const TXN_KIND_LABEL: Record<string, { text: string; color: string }> = {
  topup: { text: '充值', color: 'blue' },
  task_reward: { text: '任务奖励', color: 'green' },
  experiment_spend: { text: '实验消费', color: 'orange' },
  experiment_refund: { text: '实验退款', color: 'green' },
  earning_credit: { text: '收益入账', color: 'green' },
  withdraw_hold: { text: '提现冻结', color: 'volcano' },
  withdraw_paid: { text: '提现到账', color: 'default' },
  withdraw_reject_refund: { text: '提现驳回退款', color: 'green' },
  adjust: { text: '调整', color: 'purple' },
};

const WITHDRAW_STATUS_LABEL: Record<string, { text: string; color: string }> = {
  pending: { text: '待审核', color: 'processing' },
  approved: { text: '已通过', color: 'blue' },
  paid: { text: '已到账', color: 'success' },
  rejected: { text: '已驳回', color: 'error' },
};

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const creator = useAuthStore((s) => s.creator);
  const refreshBalances = useAuthStore((s) => s.refreshBalances);
  const updateProfile = useAuthStore((s) => s.updateProfile);
  const changePassword = useAuthStore((s) => s.changePassword);

  const [summary, setSummary] = useState<HomeSummary | null>(null);
  const [tasks, setTasks] = useState<CreatorTaskDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [claiming, setClaiming] = useState<string | null>(null);

  const [walletTab, setWalletTab] = useState<'experiment' | 'earnings'>('experiment');
  const [txns, setTxns] = useState<Txn[]>([]);
  const [txnsLoading, setTxnsLoading] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState<number | null>(50);
  const [topupLoading, setTopupLoading] = useState(false);
  const [topupInit, setTopupInit] = useState<TopupInitResp | null>(null);
  const [topupExpiresIn, setTopupExpiresIn] = useState<number>(0);
  const pollRef = useRef<number | null>(null);
  const countdownRef = useRef<number | null>(null);

  const [cards, setCards] = useState<BankCardDTO[]>([]);
  const [history, setHistory] = useState<WithdrawalDTO[]>([]);
  const [walletDetailLoading, setWalletDetailLoading] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [cardForm] = Form.useForm();
  const [withdrawForm] = Form.useForm();

  const loadAll = async () => {
    try {
      const [summaryRes, tasksRes] = await Promise.all([
        request.get<HomeSummary>('/creator/home/summary'),
        request.get<{ items: CreatorTaskDTO[] }>('/creator/tasks'),
      ]);
      setSummary(summaryRes.data as unknown as HomeSummary);
      setTasks((tasksRes.data as unknown as { items: CreatorTaskDTO[] }).items);
    } finally {
      setLoading(false);
    }
  };

  const loadTxns = async (wallet: 'experiment' | 'earnings') => {
    setTxnsLoading(true);
    try {
      const res = await request.get<{ items: Txn[]; total: number }>(
        '/creator/wallet/transactions',
        { params: { wallet, page: 1, pageSize: 50 } },
      );
      setTxns((res.data as unknown as { items: Txn[] }).items);
    } finally {
      setTxnsLoading(false);
    }
  };

  const loadWalletDetail = async () => {
    setWalletDetailLoading(true);
    try {
      const [c, w] = await Promise.all([
        request.get<BankCardDTO[]>('/creator/bank-cards'),
        request.get<WithdrawalDTO[]>('/creator/withdrawals'),
      ]);
      setCards(c.data as unknown as BankCardDTO[]);
      setHistory(w.data as unknown as WithdrawalDTO[]);
    } finally {
      setWalletDetailLoading(false);
    }
  };

  useEffect(() => {
    refreshBalances().catch(() => undefined);
    loadAll().catch(() => undefined);
    loadWalletDetail().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadTxns(walletTab).catch(() => undefined);
  }, [walletTab]);

  const claimTask = async (key: string) => {
    setClaiming(key);
    try {
      const res = await request.post<{ items: CreatorTaskDTO[] }>(
        `/creator/tasks/${key}/claim`,
      );
      setTasks((res.data as unknown as { items: CreatorTaskDTO[] }).items);
      await refreshBalances();
      message.success('已领取 ¥3 实验金');
    } catch (e) {
      message.error(e instanceof Error ? e.message : '领取失败');
    } finally {
      setClaiming(null);
    }
  };

  const stopTopupTimers = () => {
    if (pollRef.current != null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (countdownRef.current != null) {
      window.clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  };

  const closeTopup = () => {
    stopTopupTimers();
    setTopupInit(null);
    setTopupExpiresIn(0);
    setTopupOpen(false);
  };

  const startPolling = (outTradeNo: string) => {
    stopTopupTimers();
    pollRef.current = window.setInterval(async () => {
      try {
        const res = await request.get<OrderStatusResp>(`/payment/orders/${outTradeNo}`);
        const order = res.data as unknown as OrderStatusResp;
        if (order.status === 'paid') {
          stopTopupTimers();
          message.success('支付成功');
          setTopupInit(null);
          setTopupOpen(false);
          refreshBalances();
          loadTxns(walletTab);
        } else if (order.status === 'failed' || order.status === 'refunded') {
          stopTopupTimers();
          message.error('支付未完成，请重试');
          setTopupInit(null);
        }
      } catch {
        /* swallow transient errors; polling continues */
      }
    }, 3000);
  };

  const startCountdown = (expireAt: string) => {
    const tick = () => {
      const remain = Math.max(0, Math.floor((new Date(expireAt).getTime() - Date.now()) / 1000));
      setTopupExpiresIn(remain);
      if (remain === 0 && countdownRef.current != null) {
        window.clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
    tick();
    countdownRef.current = window.setInterval(tick, 1000);
  };

  const submitTopup = async () => {
    if (!topupAmount || topupAmount < 1) {
      message.error('请输入 ≥ 1 元金额');
      return;
    }
    setTopupLoading(true);
    try {
      const res = await request.post<TopupInitResp>(
        '/creator/wallet/experiment/topup',
        { amountCents: Math.round(topupAmount * 100) },
      );
      const data = res.data as unknown as TopupInitResp;
      setTopupInit(data);
      if (data.channel === 'wechat_native') {
        startCountdown(data.expireAt);
        startPolling(data.outTradeNo);
      }
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setTopupLoading(false);
    }
  };

  const simulatePaid = async () => {
    if (!topupInit || topupInit.channel !== 'dev_simulate') return;
    try {
      await request.post(topupInit.devSimulateUrl.replace(/^\/api/, ''));
      message.success('充值成功（开发环境自动模拟）');
      setTopupInit(null);
      setTopupOpen(false);
      refreshBalances();
      loadTxns(walletTab);
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  useEffect(() => () => stopTopupTimers(), []);

  const addCard = async () => {
    const v = await cardForm.validateFields();
    try {
      await request.post('/creator/bank-cards', v);
      message.success('已添加银行卡');
      setCardOpen(false);
      cardForm.resetFields();
      loadWalletDetail();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  const deleteCard = async (id: number) => {
    try {
      await request.delete(`/creator/bank-cards/${id}`);
      loadWalletDetail();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  const submitWithdraw = async () => {
    const v = await withdrawForm.validateFields();
    try {
      await request.post('/creator/withdrawals', {
        amountCents: Math.round(v.amountRmb * 100),
        bankCardId: v.bankCardId,
      });
      message.success('提现申请已提交');
      setWithdrawOpen(false);
      withdrawForm.resetFields();
      refreshBalances();
      loadWalletDetail();
      loadTxns(walletTab);
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  const openWithdraw = () => {
    if (cards.length === 0) {
      message.info('请先绑定银行卡');
      setCardOpen(true);
      return;
    }
    setWithdrawOpen(true);
  };

  if (!creator) return null;

  const claimableCount = tasks.filter((t) => t.status === 'claimable').length;
  const claimedCount = tasks.filter((t) => t.status === 'claimed').length;

  const txnColumns = [
    {
      title: '类型',
      dataIndex: 'kind',
      render: (k: string) => {
        const l = TXN_KIND_LABEL[k] ?? { text: k, color: 'default' };
        return <Tag color={l.color}>{l.text}</Tag>;
      },
    },
    {
      title: '金额',
      dataIndex: 'amountCents',
      render: (v: number) => (
        <span style={{ color: v >= 0 ? '#15803D' : '#DC2626' }}>{formatCents(v)}</span>
      ),
    },
    { title: '余额', dataIndex: 'balanceAfterCents', render: formatCents },
    { title: '备注', dataIndex: 'note', render: (v: string | null) => v || '—' },
    { title: '时间', dataIndex: 'createdAt', render: formatDate },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Greeting */}
      <div
        style={{
          background:
            'linear-gradient(135deg, #FEF3C7 0%, #FDE68A40 40%, #FBFAF7 100%)',
          borderRadius: 16,
          padding: '24px 28px',
          border: '1px solid #F0EBDE',
        }}
      >
        <Space style={{ justifyContent: 'space-between', width: '100%' }} align="start">
          <div>
            <Typography.Title level={3} style={{ margin: 0 }}>
              欢迎回来，{creator.displayName || creator.email}
            </Typography.Title>
            <Typography.Text type="secondary">
              上次登录：{creator.lastLoginAt ? formatDate(creator.lastLoginAt) : '首次登录'} ·
              账号创建于 {formatDate(creator.createdAt)}
            </Typography.Text>
          </div>
          <Space>
            <Button icon={<EditOutlined />} onClick={() => setProfileOpen(true)}>
              编辑资料
            </Button>
            <Button icon={<LockOutlined />} onClick={() => setPwdOpen(true)}>
              修改密码
            </Button>
          </Space>
        </Space>
      </div>

      {/* Task bubbles */}
      <Card
        title={
          <Space>
            <GiftOutlined style={{ color: '#D97706' }} />
            <span>新手任务</span>
            {claimableCount > 0 && (
              <Tag color="gold" style={{ margin: 0 }}>
                {claimableCount} 个可领取
              </Tag>
            )}
            {claimedCount === tasks.length && tasks.length > 0 && (
              <Tag color="success" style={{ margin: 0 }}>
                已全部完成
              </Tag>
            )}
          </Space>
        }
      >
        <Typography.Paragraph type="secondary" style={{ marginTop: 0, fontSize: 12 }}>
          完成任务即可领取实验金（每项 ¥3），用于在实验台和套图调用里扣费
        </Typography.Paragraph>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <Spin />
          </div>
        ) : (
          <Row gutter={[12, 12]}>
            {tasks.map((t) => (
              <Col key={t.key} xs={24} md={8}>
                <TaskBubble
                  task={t}
                  claiming={claiming === t.key}
                  onClaim={() => claimTask(t.key)}
                  onNavigate={() => navigate(t.ctaPath)}
                />
              </Col>
            ))}
          </Row>
        )}
      </Card>

      {/* Balance + actions */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="实验金余额"
              value={formatCents(creator.experimentBalanceCents)}
              valueStyle={{ color: '#B45309' }}
              prefix={<WalletOutlined />}
            />
            <Space style={{ marginTop: 12 }}>
              <Button size="small" type="primary" onClick={() => setTopupOpen(true)}>
                充值
              </Button>
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="收益余额（可提现）"
              value={formatCents(creator.earningsBalanceCents)}
              valueStyle={{ color: '#15803D' }}
              prefix={<RiseOutlined />}
            />
            <Space style={{ marginTop: 12 }}>
              <Button
                size="small"
                type="primary"
                icon={<BankOutlined />}
                onClick={openWithdraw}
              >
                提现到银行卡
              </Button>
            </Space>
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card>
            <Statistic
              title="实验 / 套图"
              value={
                loading
                  ? '—'
                  : `${summary?.experimentsCount ?? 0} / ${summary?.publishedBundlesCount ?? 0}`
              }
              valueStyle={{ color: '#475569' }}
              prefix={<ExperimentOutlined />}
            />
            <Button type="link" onClick={() => navigate('/experiments')} style={{ padding: 0, marginTop: 8 }}>
              实验台 →
            </Button>
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card>
            <Statistic
              title="销量 / 收益"
              value={
                loading
                  ? '—'
                  : `${summary?.totalSalesCount ?? 0} · ${formatCents(summary?.totalEarningsCents ?? 0)}`
              }
              valueStyle={{ color: '#15803D' }}
              prefix={<AppstoreOutlined />}
            />
            <Button type="link" onClick={() => navigate('/bundles')} style={{ padding: 0, marginTop: 8 }}>
              我的套图 →
            </Button>
          </Card>
        </Col>
      </Row>

      {!creator.realNameVerified && creator.earningsBalanceCents > 0 && (
        <Alert
          type="info"
          showIcon
          message="首次提现前需要完成实名认证与绑定银行卡"
          description="点击上方「提现到银行卡」进入绑定流程"
        />
      )}

      {/* Wallet transactions */}
      <Card title="钱包流水">
        <Tabs
          activeKey={walletTab}
          onChange={(k) => setWalletTab(k as 'experiment' | 'earnings')}
          items={[
            { key: 'experiment', label: '实验金流水' },
            { key: 'earnings', label: '收益流水' },
          ]}
        />
        <Table
          rowKey="id"
          columns={txnColumns}
          dataSource={txns}
          loading={txnsLoading}
          pagination={false}
          locale={{ emptyText: <Empty description="暂无流水" /> }}
        />
      </Card>

      {/* Bank cards */}
      <Card title="我的银行卡" extra={<Button onClick={() => setCardOpen(true)}>添加</Button>}>
        <List
          loading={walletDetailLoading}
          dataSource={cards}
          locale={{ emptyText: '还没有绑定的银行卡' }}
          renderItem={(c) => (
            <List.Item
              actions={[
                <a key="del" style={{ color: '#DC2626' }} onClick={() => deleteCard(c.id)}>
                  删除
                </a>,
              ]}
            >
              <List.Item.Meta
                title={
                  <Space>
                    <span>{c.bankName}</span>
                    <span>**** **** **** {c.accountLast4}</span>
                    {c.isPrimary && <Tag color="gold">默认</Tag>}
                  </Space>
                }
                description={`持卡人：${c.holderName}`}
              />
            </List.Item>
          )}
        />
      </Card>

      {/* Withdraw history */}
      <Card title="提现记录">
        <List
          dataSource={history}
          locale={{ emptyText: '还没有提现记录' }}
          renderItem={(w) => {
            const s = WITHDRAW_STATUS_LABEL[w.status] ?? { text: w.status, color: 'default' };
            return (
              <List.Item>
                <List.Item.Meta
                  title={
                    <Space>
                      <span>{formatCents(w.amountCents)}</span>
                      <Tag color={s.color}>{s.text}</Tag>
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size={2}>
                      <span>
                        到账账号：{w.bankName} **** {w.accountLast4}
                      </span>
                      <span style={{ color: '#9CA3AF' }}>
                        发起时间：{formatDate(w.createdAt)}
                      </span>
                      {w.rejectionReason && (
                        <span style={{ color: '#DC2626' }}>
                          驳回理由：{w.rejectionReason}
                        </span>
                      )}
                    </Space>
                  }
                />
              </List.Item>
            );
          }}
        />
      </Card>

      <ProfileModal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        initialDisplayName={creator.displayName ?? ''}
        initialAvatarUrl={creator.avatarUrl}
        initialAvatarCosKey={creator.avatarCosKey}
        onSave={async ({ displayName, avatarCosKey }) => {
          await updateProfile({ displayName, avatarCosKey });
          message.success('资料已更新');
          setProfileOpen(false);
        }}
      />
      <PasswordModal
        open={pwdOpen}
        onClose={() => setPwdOpen(false)}
        onSave={async (oldPwd, newPwd) => {
          await changePassword(oldPwd, newPwd);
          message.success('密码已更新');
          setPwdOpen(false);
        }}
      />

      <Modal
        title="充值实验金"
        open={topupOpen}
        onOk={topupInit ? undefined : submitTopup}
        confirmLoading={topupLoading}
        onCancel={closeTopup}
        footer={
          topupInit?.channel === 'wechat_native'
            ? [
                <Button key="cancel" onClick={closeTopup}>
                  取消
                </Button>,
              ]
            : topupInit?.channel === 'dev_simulate'
              ? [
                  <Button key="cancel" onClick={closeTopup}>
                    取消
                  </Button>,
                  <Button key="simulate" type="primary" onClick={simulatePaid}>
                    模拟支付成功
                  </Button>,
                ]
              : undefined
        }
      >
        {!topupInit && (
          <>
            <Typography.Paragraph type="secondary">
              实验金按人民币结算，用于支付 fal.ai 的实际成本。生产环境会生成微信支付二维码，手机微信扫码即可完成支付。
            </Typography.Paragraph>
            <InputNumber
              min={1}
              max={10000}
              step={10}
              value={topupAmount}
              onChange={(v) => setTopupAmount(v ? Number(v) : null)}
              addonAfter="元"
              style={{ width: '100%' }}
            />
          </>
        )}

        {topupInit?.channel === 'wechat_native' && (
          <div style={{ textAlign: 'center' }}>
            <Typography.Title level={4} style={{ margin: '0 0 4px' }}>
              ¥{(topupInit.amountCents / 100).toFixed(2)}
            </Typography.Title>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              订单号：{topupInit.outTradeNo}
            </Typography.Text>
            <div
              style={{
                padding: 16,
                margin: '16px auto',
                background: '#fff',
                border: '1px solid #E7E2D6',
                borderRadius: 12,
                display: 'inline-block',
              }}
            >
              <QRCodeSVG value={topupInit.codeUrl} size={220} level="M" />
            </div>
            <Typography.Paragraph style={{ marginTop: 8 }}>
              请使用 <strong>微信「扫一扫」</strong> 扫描上方二维码完成支付
            </Typography.Paragraph>
            <Typography.Text type="secondary">
              支付成功后页面会自动刷新
              {topupExpiresIn > 0 && ` · 二维码剩余 ${Math.floor(topupExpiresIn / 60)}分${topupExpiresIn % 60}秒`}
            </Typography.Text>
          </div>
        )}

        {topupInit?.channel === 'dev_simulate' && (
          <div>
            <Alert
              type="info"
              showIcon
              message="开发环境：未配置微信支付商户证书"
              description="当前没有配置 WECHAT_PAY_* 环境变量，无法拉起真实微信支付。点击下方「模拟支付成功」按钮即视作充值到账。"
              style={{ marginBottom: 12 }}
            />
            <div>订单号：{topupInit.outTradeNo}</div>
            <div>金额：¥{(topupInit.amountCents / 100).toFixed(2)}</div>
          </div>
        )}
      </Modal>

      <Modal
        title="绑定银行卡"
        open={cardOpen}
        onOk={addCard}
        onCancel={() => setCardOpen(false)}
        okText="保存"
      >
        <Alert
          type="warning"
          showIcon
          message="账号会用加密方式保存，仅管理员打款时解密查看。"
          style={{ marginBottom: 16 }}
        />
        <Form form={cardForm} layout="vertical">
          <Form.Item
            name="holderName"
            label="持卡人姓名"
            rules={[{ required: true, message: '必填' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="bankName"
            label="开户行"
            rules={[{ required: true, message: '必填' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="accountNumber"
            label="卡号"
            rules={[{ required: true, pattern: /^\d{8,30}$/, message: '请填写正确的卡号' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="idCardLast4" label="身份证后 4 位（选填）">
            <Input maxLength={4} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="申请提现"
        open={withdrawOpen}
        onOk={submitWithdraw}
        onCancel={() => setWithdrawOpen(false)}
        okText="提交"
      >
        <Form form={withdrawForm} layout="vertical">
          <Form.Item
            name="amountRmb"
            label="金额（元）"
            rules={[{ required: true, message: '请输入金额' }]}
          >
            <InputNumber
              min={100}
              max={creator.earningsBalanceCents / 100}
              step={10}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="bankCardId"
            label="到账银行卡"
            rules={[{ required: true, message: '必选' }]}
          >
            <Input.Group compact>
              {cards.map((c) => (
                <Tag.CheckableTag
                  key={c.id}
                  checked={withdrawForm.getFieldValue('bankCardId') === c.id}
                  onChange={() => withdrawForm.setFieldsValue({ bankCardId: c.id })}
                >
                  {c.bankName} **** {c.accountLast4}
                </Tag.CheckableTag>
              ))}
            </Input.Group>
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

/** Orange bubble for claimable / pending, green bubble for claimed. */
const TaskBubble: React.FC<{
  task: CreatorTaskDTO;
  claiming: boolean;
  onClaim: () => void;
  onNavigate: () => void;
}> = ({ task, claiming, onClaim, onNavigate }) => {
  const isClaimable = task.status === 'claimable';
  const isClaimed = task.status === 'claimed';

  // Green bubble
  if (isClaimed) {
    return (
      <div
        style={{
          position: 'relative',
          background:
            'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 60%, #A7F3D0 100%)',
          border: '1px solid #A7F3D0',
          borderRadius: 16,
          padding: '14px 16px',
          height: '100%',
          boxShadow: '0 1px 2px rgba(6, 95, 70, 0.05)',
        }}
      >
        <Space align="start" style={{ width: '100%' }}>
          <CheckCircleFilled style={{ color: '#10B981', fontSize: 22 }} />
          <div style={{ flex: 1 }}>
            <Typography.Text strong style={{ color: '#065F46' }}>
              {task.title}
            </Typography.Text>
            <div style={{ fontSize: 12, color: '#059669', marginTop: 2 }}>
              已领取 ¥{(task.rewardCents / 100).toFixed(2)}
              {task.claimedAt && ` · ${formatDate(task.claimedAt)}`}
            </div>
          </div>
        </Space>
      </div>
    );
  }

  // Orange bubble (either pending or claimable)
  return (
    <div
      style={{
        position: 'relative',
        background: isClaimable
          ? 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 60%, #FCD34D 100%)'
          : 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)',
        border: `1px solid ${isClaimable ? '#FCD34D' : '#FDE68A'}`,
        borderRadius: 16,
        padding: '14px 16px',
        height: '100%',
        boxShadow: isClaimable
          ? '0 4px 12px rgba(217, 119, 6, 0.18), 0 1px 3px rgba(180, 83, 9, 0.1)'
          : '0 1px 2px rgba(180, 83, 9, 0.05)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div>
        <Space size={6}>
          <Typography.Text strong style={{ color: '#92400E' }}>
            {task.title}
          </Typography.Text>
          <Tag color="gold" style={{ margin: 0, fontSize: 11 }}>
            +¥{(task.rewardCents / 100).toFixed(2)}
          </Tag>
        </Space>
        <div style={{ fontSize: 12, color: '#B45309', marginTop: 4 }}>{task.description}</div>
      </div>
      <div style={{ marginTop: 'auto' }}>
        {isClaimable ? (
          <Button
            type="primary"
            size="small"
            loading={claiming}
            onClick={onClaim}
            icon={<GiftOutlined />}
            block
          >
            立即领取 ¥{(task.rewardCents / 100).toFixed(2)}
          </Button>
        ) : (
          <Button size="small" onClick={onNavigate} block>
            去完成
          </Button>
        )}
      </div>
    </div>
  );
};

const ProfileModal: React.FC<{
  open: boolean;
  onClose: () => void;
  initialDisplayName: string;
  initialAvatarUrl: string | null;
  initialAvatarCosKey: string | null;
  onSave: (patch: { displayName: string; avatarCosKey: string | null }) => Promise<void>;
}> = ({ open, onClose, initialDisplayName, initialAvatarUrl, initialAvatarCosKey, onSave }) => {
  const [form] = Form.useForm<{ displayName: string }>();
  const [previewUrl, setPreviewUrl] = useState<string | null>(initialAvatarUrl);
  const [cosKey, setCosKey] = useState<string | null>(initialAvatarCosKey);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({ displayName: initialDisplayName });
      setPreviewUrl(initialAvatarUrl);
      setCosKey(initialAvatarCosKey);
    }
  }, [open, initialDisplayName, initialAvatarUrl, initialAvatarCosKey, form]);

  const customUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    const f = file as File;
    if (!f.type.startsWith('image/')) {
      message.error('请选择图片文件');
      onError?.(new Error('invalid type'));
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      message.error('头像不能超过 5MB');
      onError?.(new Error('too large'));
      return;
    }
    setUploading(true);
    try {
      // 1. request pre-signed URL
      const presignRes = await request.post<{ uploadUrl: string; cosKey: string }>(
        '/cos/upload-url',
        { filename: f.name, contentType: f.type },
      );
      const { uploadUrl, cosKey: newKey } = presignRes.data as unknown as {
        uploadUrl: string;
        cosKey: string;
      };
      // 2. PUT binary to COS
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        body: f,
        headers: { 'Content-Type': f.type },
      });
      if (!putRes.ok) throw new Error(`COS upload failed: ${putRes.status}`);
      // 3. resolve access URL for preview
      const accessRes = await request.post<{ accessUrl: string }>(
        '/cos/access-url',
        { cosKey: newKey },
      );
      const { accessUrl } = accessRes.data as unknown as { accessUrl: string };
      setCosKey(newKey);
      setPreviewUrl(accessUrl);
      onSuccess?.(null as unknown as Response, f as unknown as XMLHttpRequest);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '上传失败');
      onError?.(e instanceof Error ? e : new Error('upload failed'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="编辑资料"
      okButtonProps={{ loading: uploading, disabled: uploading }}
      onOk={async () => {
        const values = await form.validateFields();
        try {
          await onSave({ displayName: values.displayName.trim(), avatarCosKey: cosKey });
        } catch (e) {
          message.error(e instanceof Error ? e.message : '保存失败');
        }
      }}
    >
      <Form form={form} layout="vertical">
        <Form.Item label="头像">
          <Space align="center" size="large">
            <Avatar size={72} src={previewUrl ?? undefined} icon={<UserOutlined />} />
            <Space>
              <Upload
                accept="image/*"
                showUploadList={false}
                customRequest={customUpload}
                disabled={uploading}
              >
                <Button icon={<UploadOutlined />} loading={uploading}>
                  {previewUrl ? '更换' : '上传'}
                </Button>
              </Upload>
              {previewUrl && (
                <Button
                  type="text"
                  danger
                  onClick={() => {
                    setPreviewUrl(null);
                    setCosKey(null);
                  }}
                >
                  移除
                </Button>
              )}
            </Space>
          </Space>
        </Form.Item>
        <Form.Item name="displayName" label="昵称" rules={[{ required: true, message: '请输入昵称' }]}>
          <Input placeholder="您的昵称，会出现在套图署名里" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const PasswordModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (oldPwd: string, newPwd: string) => Promise<void>;
}> = ({ open, onClose, onSave }) => {
  const [form] = Form.useForm<{ oldPassword: string; newPassword: string; confirm: string }>();
  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="修改密码"
      onOk={async () => {
        const values = await form.validateFields();
        if (values.newPassword !== values.confirm) {
          message.error('两次输入的新密码不一致');
          return;
        }
        try {
          await onSave(values.oldPassword, values.newPassword);
          form.resetFields();
        } catch (e) {
          message.error(e instanceof Error ? e.message : '修改失败');
        }
      }}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="oldPassword" label="当前密码" rules={[{ required: true }]}>
          <Input.Password />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label="新密码"
          rules={[{ required: true, min: 8, message: '至少 8 位' }]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item name="confirm" label="确认新密码" rules={[{ required: true }]}>
          <Input.Password />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default HomePage;
